import React, { useEffect, useRef } from 'react';
import { View, Text, Pressable, Animated, Platform } from 'react-native';
import { Toast as ToastType } from '../../shared/types/toast';
import { useStyles } from '../../hooks/useStyles';
import { addColorOpacity } from '../../shared/utils/styles';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
  position: ToastPosition;
}

type ToastPosition = 'top' | 'bottom';

export function Toast({ toast, onDismiss, position }: ToastProps) {
  const styles = useStyles();
  const slideAnim = useRef(new Animated.Value(position === 'top' ? -100 : 100)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dismiss = React.useCallback(() => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: position === 'top' ? -100 : 100,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss(toast.id);
    });
  }, [slideAnim, opacityAnim, position, onDismiss, toast.id]);

  useEffect(() => {
    // Slide in and fade in
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 50,
        friction: 8,
      }),
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto dismiss
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      timeoutRef.current = setTimeout(() => {
        dismiss();
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dismiss, slideAnim, opacityAnim, toast.duration]);

  const getToastColors = () => {
    switch (toast.type) {
      case 'success':
        return {
          backgroundColor: styles.tokens.color.success,
          textColor: styles.tokens.color.onSuccess,
          iconColor: styles.tokens.color.onSuccess,
        };
      case 'error':
        return {
          backgroundColor: styles.tokens.color.error,
          textColor: styles.tokens.color.onError,
          iconColor: styles.tokens.color.onError,
        };
      case 'warning':
        // Use cream color for warning (yellow/amber-like)
        return {
          backgroundColor: '#FDE68A', // cream.200 from design tokens
          textColor: '#78350F', // brown.800 equivalent for contrast
          iconColor: '#78350F',
        };
      case 'info':
      default:
        return {
          backgroundColor: styles.tokens.color.primary,
          textColor: styles.tokens.color.onPrimary,
          iconColor: styles.tokens.color.onPrimary,
        };
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const colors = getToastColors();
  const icon = getIcon();

  return (
    <Animated.View
      style={{
        transform: [{ translateY: slideAnim }],
        opacity: opacityAnim,
        marginBottom: styles.utils.spacing.sm,
      }}
    >
      <Pressable
        onPress={dismiss}
        style={{
          backgroundColor: colors.backgroundColor,
          borderRadius: styles.utils.radius.md,
          paddingHorizontal: styles.utils.spacing.lg,
          paddingVertical: styles.utils.spacing.md,
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 56,
          borderWidth: 1,
          borderColor: addColorOpacity(colors.textColor, 0.2),
          ...Platform.select({
            web: {
              boxShadow: `0 4px 12px ${addColorOpacity('#000', 0.15)}`,
              cursor: 'pointer',
            },
            default: {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.15,
              shadowRadius: 8,
              elevation: 4,
            },
          }),
        }}
      >
        {/* Icon */}
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: styles.utils.radius.full,
            backgroundColor: addColorOpacity(colors.textColor, 0.2),
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: styles.utils.spacing.md,
          }}
        >
          <Text
            style={{
              color: colors.iconColor,
              fontSize: styles.utils.typography.sizes.sm,
              fontWeight: styles.utils.typography.weights.bold,
            }}
          >
            {icon}
          </Text>
        </View>

        {/* Content */}
        <View style={{ flex: 1 }}>
          {toast.title && (
            <Text
              style={{
                fontSize: styles.utils.typography.sizes.sm,
                fontWeight: styles.utils.typography.weights.semibold,
                color: colors.textColor,
                marginBottom: styles.utils.spacing.xs / 2,
                fontFamily: styles.utils.typography.body.fontFamily,
              }}
            >
              {toast.title}
            </Text>
          )}
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.base,
              color: colors.textColor,
              fontFamily: styles.utils.typography.body.fontFamily,
              lineHeight: styles.utils.typography.sizes.base * 1.5,
            }}
          >
            {toast.message}
          </Text>
        </View>
      </Pressable>
    </Animated.View>
  );
}
